/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import { Icon, Badge } from 'sarsaparilla';
import { MenuButton } from 'sarsaparilla/src/menu-button';

import { FlexDates } from 'ui-navigation/src/components/homepage/FlexDates/FlexDates';

import * as searchActions from '../actions/search';
import * as types from '../constants/index';
import SearchInputContainer from './SearchInputContainer';
import { SearchCriteriaDates } from '../components/SearchCriteriaDates';
import { QuickFilterActivityTags } from '../components/QuickFilterActivityTags';
import { searchDate } from '../utils/dateUtils';
import { ActivityTagFeedbackModal } from '../components/ActivityTagFeedbackModal/ActivityTagFeedbackModal';

class SearchCriteriaInputs extends React.Component {
    static propTypes = {
        search: PropTypes.object,
        dispatch: PropTypes.func,
        page: PropTypes.string,
        isMainSearchCriteria: PropTypes.bool,
        onApplyFilters: PropTypes.func,
        isCampingSearch: PropTypes.bool,
        flags: PropTypes.object,
        checkin_time: PropTypes.any,
    };

    static defaultProps = {
        isMainSearchCriteria: true,
    };

    constructor(props) {
        super(props);
        this.onToggleFilters = this.onToggleFilters.bind(this);
        this.onFilterToggle = this.onFilterToggle.bind(this);
    }

    componentDidMount() {
        if (!this.props.search.checkin_time) {
            this.props.dispatch(searchActions.updateSelectedDate('', true));
        }
        if (!this.props.search.checkout_time) {
            this.props.dispatch(searchActions.updateSelectedDate('', false));
        }
    }

    componentDidUpdate(prevProps) {
        const { search } = this.props;

        if (
            prevProps.search.checkin_time !== search.checkin_time &&
            isEmpty(search.checkin_time) &&
            isEmpty(search.avail_months) &&
            (!search.include_unavailable ||
                !search.include_notreservable ||
                !search.include_partially_available)
        ) {
            this.updateShowAvailableFilters(false);
        }
    }

    onFilterToggle(filterName, item) {
        this.props.dispatch(searchActions.toggleSearchFilter(filterName, item));
    }

    onToggleFilters() {
        // close filters and refresh results
        this.props.dispatch(
            searchActions.updateSearchCriteria(
                'filtersVisible',
                !this.props.search.filtersVisible
            )
        );
        this.props.dispatch(
            searchActions.updateSearchCriteria('inventoryTypeFiltersVisible', false)
        );

        if (this.props.onApplyFilters) {
            this.props.onApplyFilters();
        }
    }

    showFiltersOnThisPage() {
        switch (this.props.page) {
            case types.CAMPSITE_PAGE:
            case types.CAMPGROUND_PAGE:
            case types.CAMPGROUND_AVAILABILITY_PAGE:
                return false;
            default:
                return true;
        }
    }

    updateShowAvailableFilters(value) {
        const uiFilters = {
            ...this.props.search?.uiFilters,
        };
        this.props.dispatch(
            searchActions.updateSearchCriterias({
                include_unavailable: !value,
                include_notreservable: !value,
                include_partially_available: !value,
                start: null,
                uiFilters,
            })
        );
        this.props.dispatch(searchActions.navigateToSearchResults());
    }

    onDatesChange = () => {
        const showAvailable =
            !get(this.props.search, 'include_unavailable', true) &&
            !get(this.props.search, 'include_notreservable', true) &&
            !get(this.props.search, 'include_partially_available', true) &&
            !(
                isEmpty(this.props.search.checkin_time) &&
                isEmpty(this.props.search.avail_months)
            );

        if (!showAvailable) {
            this.updateShowAvailableFilters(true);
        }
    };

    parseFlexDatesParams = (months, duration) => {
        let monthsArray = [];
        if (months) {
            months.map((item) => {
                const av = searchDate(item)
                    .startOf('month')
                    .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
                monthsArray = [...monthsArray, av];
                return item;
            });
        }
        return { avail_months: monthsArray, avail_nights: duration };
    };

    onFlexFiltersChange = (flexDatesFilter) => {
        const startDate = flexDatesFilter?.startDate || null;
        const endDate = flexDatesFilter?.endDate || null;
        let startDateStr = startDate
            ? searchDate(flexDatesFilter.startDate)
                  .startOf('day')
                  .format(types.CAL_DATE_FORMAT)
            : null;
        let endDateStr = endDate
            ? searchDate(flexDatesFilter.endDate, types.CAL_DATE_FORMAT)
                  .startOf('day')
                  .format(types.CAL_DATE_FORMAT)
            : null;
        const { avail_months, avail_nights } = this.parseFlexDatesParams(
            flexDatesFilter?.months,
            flexDatesFilter?.duration
        );
        let sort = this.props.search.sort;

        this.props.dispatch(searchActions.updateSelectedDate(startDateStr, true));
        this.props.dispatch(searchActions.updateSelectedDate(endDateStr, false));

        if (startDateStr) {
            if (!sort || sort === '' || sort === 'score') {
                // default to available sort if dates are set
                sort = 'available';
            }
        } else if (sort === 'available') {
            // clear available sort if no dates are set
            sort = null;
        }
        if (flexDatesFilter?.months && flexDatesFilter?.duration) {
            sort = 'available';
        }

        let avMonths = avail_months;
        let avNights = avail_nights;
        if (isEmpty(flexDatesFilter)) {
            startDateStr = '';
            endDateStr = '';
            avMonths = [];
            avNights = '';
        }

        let showAvailable = {};
        if (startDate && endDate) {
            showAvailable = {
                include_unavailable: false,
                include_notreservable: false,
                include_partially_available: false,
            };
        }

        this.props.dispatch(
            searchActions.updateSearchCriterias({
                start: null,
                cursor_mark: null,
                sort,
                checkin_time: startDateStr,
                checkout_time: endDateStr,
                flex_start_range: flexDatesFilter?.startRange || '',
                flex_end_range: flexDatesFilter?.endRange || '',
                avail_nights: avNights || '',
                avail_months: avMonths || [],
                ...showAvailable,
            })
        );

        this.props.onApplyFilters();
    };

    getFlexDates = (props) => {
        const flexDates = {};

        if (props.search.checkin_time) {
            flexDates.startDate = searchDate(props.search.checkin_time);
        }
        if (props.search.checkout_time) {
            flexDates.endDate = searchDate(props.search.checkout_time);
        }
        if (props.search.avail_nights) {
            flexDates.duration = props.search.avail_nights;
        }
        if (props.search.flex_start_range) {
            flexDates.startRange = props.search.flex_start_range;
        }
        if (props.search.flex_end_range) {
            flexDates.endRange = props.search.flex_end_range;
        }

        if (!isEmpty(props.search.avail_months)) {
            flexDates.months = props.search.avail_months?.map((item) =>
                searchDate(item).utc().format('MM-YYYY')
            );
        }
        return flexDates;
    };

    renderMainSearchCriteria() {
        let filterCount = 0;

        if (this.props.search && this.props.search.uiFilters) {
            filterCount += Object.keys(this.props.search.uiFilters.occupants).length;

            filterCount += Object.keys(this.props.search.uiFilters.activities).length;
            if (this.props.search.uiFilters.activityTags) {
                filterCount += Object.keys(
                    this.props.search.uiFilters.activityTags
                ).length;
            }
            filterCount += Object.keys(this.props.search.uiFilters.agencies).length;

            const fg = get(this.props.search, 'fg', []);
            filterCount += fg.length;
            filterCount += Object.keys(
                this.props.search.uiFilters.campsiteVehicleLength
            ).length;

            filterCount += Object.keys(this.props.search.uiFilters.tourTypes).length;
            filterCount += Object.keys(this.props.search.uiFilters.tourAccessible).length;
            filterCount += Object.keys(this.props.search.uiFilters.tourDifficulty).length;
            filterCount += Object.keys(this.props.search.uiFilters.tourDuration).length;
            filterCount += Object.keys(this.props.search.uiFilters.tourPrice).length;
            filterCount += Object.keys(this.props.search.uiFilters.tourTime).length;

            if (this.props.search.uiFilters?.tourTimes) {
                filterCount += Object.keys(this.props.search.uiFilters.tourTimes).length;
            }

            filterCount += Object.keys(this.props.search.uiFilters.permitTypes).length;
            filterCount += Object.keys(this.props.search.uiFilters.inventoryTypes).length;
        }

        const flexDatesFilter = this.getFlexDates(this.props);

        // Search results header filters
        return (
            <div className="header-filters" role="group" aria-label="Search criteria">
                <SearchInputContainer />

                {this.props.isCampingSearch ? (
                    <div className="search-flex-wrapper">
                        <FlexDates
                            monthsLimit={4}
                            onFiltersChange={this.onFlexFiltersChange}
                            filters={flexDatesFilter}
                            isSearch
                            showClear
                        />
                    </div>
                ) : (
                    <div className="date-range-picker-wrap">
                        <SearchCriteriaDates
                            onApplyFilters={this.props.onApplyFilters}
                            onDatesChange={this.onDatesChange}
                        />
                    </div>
                )}

                {this.props.isCampingSearch &&
                    this.props.flags?.activityTagsMvp?.quickFilterEnabled && (
                        <QuickFilterActivityTags
                            uiFilters={this.props.search.uiFilters}
                            onFilterToggle={this.onFilterToggle}
                            onApplyFilters={() => {
                                this.props.dispatch(
                                    searchActions.navigateToSearchResults()
                                );
                            }}
                        />
                    )}

                <div className="show-filters-dropdown-wrap">
                    <div className="rec-select-holder show-filters-dropdown">
                        <MenuButton
                            aria-label={
                                this.props.search.filtersVisible
                                    ? 'Hide filters'
                                    : 'Show filters'
                            }
                            aria-expanded={this.props.search.filtersVisible}
                            aria-controls="search-filters-panel"
                            onClick={this.onToggleFilters}
                            id="search-header-filters-btn"
                            data-ga-tracking-id="554715340412"
                        >
                            <Icon iconName="filter-list" />
                            {this.props.search.filtersVisible
                                ? 'Hide Filters'
                                : 'More Filters'}
                            {filterCount > 0 && (
                                <Badge appearance="primary" className="ml-half">
                                    {filterCount}
                                </Badge>
                            )}
                        </MenuButton>
                    </div>
                </div>

                <ActivityTagFeedbackModal />
            </div>
        );
    }

    render() {
        const showFilters = this.showFiltersOnThisPage();
        if (showFilters) {
            if (this.props.isMainSearchCriteria) {
                return this.renderMainSearchCriteria();
            }
            return null;
        }
        return null;
    }
}

function select(state) {
    return {
        account: get(state, 'auth.account', null),
        search: state.search,
        searchAggregations: state.searchAggregations,
    };
}

export default connect(select)(SearchCriteriaInputs);

// cSpell:ignore checkin_time, criterias, notreservable
